import { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getAllEmployeesApi } from '../../../Api/Employee';
import SideModal from '../../general/Sidemodal';
import EmployeeForm from './EmployeeForm';

interface Props {
    url: string;
};
type User = {
    id: string;
    name: string;
    email: string;
    mobile: string;
    gender: number | null;
    address: string;
    roleType: string;
};
export interface EmployeeInterface {
    id: string;
    userId: string;
    employeeType: string;
    permanentAddress: string;
    isActive: boolean;
    salary: number;
    hourlyRate: number;
    joiningDate: string;
    leavingDate?: string | null;
    updatedBy: string;
    updatedOn: string;
    user: User;
}

const Employee = ({ url }: Props) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [Employees, setEmployees] = useState<EmployeeInterface[]>([]);
    const [loader, setLoader] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    const fetchData = async (page: number) => {
        try {
            setLoader(true);
            const response: any = await getAllEmployeesApi(url, page, itemsPerPage, search);
            setEmployees(response.employees || []);
            setTotalPages(Math.ceil(response.totalCount / itemsPerPage));
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch data");
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, search]);

    const [showModal, setShowModal] = useState(false);
    const [editEmployeeId, setEditEmployeeId] = useState<string | null>(null);

    const handleAddClick = () => {
        setEditEmployeeId(null);
        setShowModal(true);
    };

    const handleEditClick = (id: string) => {
        setEditEmployeeId(id);
        setShowModal(true);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setSearch(e.target.value);
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setCurrentPage(1);
        fetchData(currentPage);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="px-2 md:px-4 mt-6">
            <div className='flex flex-row items-center '>
                <h2 className="text-3xl font-bold text-blue-600 mb-5">Employee</h2>
                <button
                    type="button"
                    className=" w-fit px-3 py-2 mx-3 mb-4 tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
                    onClick={handleAddClick}
                >
                    <FaPlus className="w-3 h-3" />
                </button>
            </div>

            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="w-full md:flex-grow mb-5">
                <label
                    htmlFor="default-search"
                    className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
                >
                    Search
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                        <svg
                            className="w-4 h-4 text-gray-500 dark:text-gray-400"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 20"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                            />
                        </svg>
                    </div>
                    <input
                        type="search"
                        id="default-search"
                        className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Search by Name, Email, Mobile, EmployeeType, Salary"
                        value={search}
                        onChange={handleSearchChange}
                        required
                    />
                    <button
                        type="submit"
                        className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                    >
                        Search
                    </button>
                </div>
            </form>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Action</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Name</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Mobile</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Employee Type</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Salary</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Hourly Rate</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Joining Date</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Leaving Date</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Current Address</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Permanent Address</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Email</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Gender</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Is Active</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                        {loader ? (
                            <tr>
                                <td colSpan={13} className="py-3 text-center">
                                    <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin mx-auto"></div>
                                </td>
                            </tr>
                        ) : (
                            Employees.map((item) => (
                                <tr key={item.user.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                                        <button
                                            onClick={() => handleEditClick(item.user.id)}
                                            className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                            </svg>
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 text-sm font-medium text-gray-700 whitespace-nowrap">{item.user.name}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.user.mobile}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.employeeType}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">₹{item.salary}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">₹{item.hourlyRate ? item.hourlyRate : ' -'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.joiningDate}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.leavingDate || '-'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.user.address}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.permanentAddress}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">{item.user.email}</td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                                        {item.user.gender === 1 ? 'Male' : item.user.gender === 2 ? 'Female' : ''}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                                        {item.isActive ? "Yes" : "No"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
                <button
                    disabled={currentPage === 1}
                    onClick={handlePreviousPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                    </svg>
                    <span>Previous</span>
                </button>
                <div className="flex flex-wrap items-center justify-center gap-x-2">
                    {[...Array(Math.max(totalPages, 1))].map((_, index) => (
                        <button
                            key={index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-2 py-1 text-sm ${currentPage === index + 1
                                ? "text-blue-500 bg-blue-100"
                                : "text-gray-500 hover:bg-gray-100"
                                } rounded-md`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
                <button
                    disabled={currentPage === totalPages}
                    onClick={handleNextPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <span>Next</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                    </svg>
                </button>
            </div>



            <SideModal isOpen={showModal} onClose={() => setShowModal(false)} >
                <EmployeeForm url={url} onClose={() => setShowModal(false)} editEmployeeId={editEmployeeId} />
            </SideModal>

        </div>
    );
};

export default Employee;